/* GENERATED FILE — do not edit.
   Source: shared-ui/ui/StatTile.js
   Run "node shared-ui/sync.js" after editing the source. */
import React, { useEffect, useRef, useState } from 'react';

const DURATION = 850;

function decimalsOf(raw) {
  const str = String(raw);
  const dot = str.indexOf('.');
  return dot === -1 ? 0 : str.length - dot - 1;
}

function prefersReducedMotion() {
  return !!(
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * label – mono uppercase caption
 * value – the number (count-up) or any string (printed as-is)
 * note  – small line underneath
 * index – optional stagger position, matching the deck's 90ms cascade
 */
export default function StatTile({ label, value, note, index = 0 }) {
  const target = Number(value);
  const animatable =
    value !== null && value !== undefined && value !== '' && Number.isFinite(target);

  const finalText = animatable ? target.toFixed(decimalsOf(value)) : String(value == null ? '' : value);

  const [shown, setShown] = useState(animatable ? '0' : finalText);
  const finalRef = useRef(finalText);
  finalRef.current = finalText;

  useEffect(() => {
    if (!animatable) {
      setShown(finalRef.current);
      return undefined;
    }

    const digits = decimalsOf(value);
    let done = false;
    let raf = 0;

    const settle = () => setShown(finalRef.current);

    const onVisibility = () => {
      // Hidden tabs throttle rAF to a crawl — snap to the truth instead.
      if (document.hidden) {
        done = true;
        settle();
      }
    };

    // Motion never owns the value: no motion means the value is simply there.
    if (prefersReducedMotion() || document.hidden) {
      settle();
      return undefined;
    }

    const delay = 90 * index;
    let start = null;

    const step = (ts) => {
      if (done) return;
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / DURATION);
      const eased = 1 - Math.pow(1 - p, 3);
      setShown((target * eased).toFixed(digits));
      if (p < 1) raf = requestAnimationFrame(step);
      else done = true;
    };

    const startTimer = setTimeout(() => {
      raf = requestAnimationFrame(step);
    }, delay);

    // Whatever the animation managed, the true value lands on time.
    const settleTimer = setTimeout(() => {
      done = true;
      settle();
    }, delay + DURATION + 60);

    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      done = true;
      clearTimeout(startTimer);
      clearTimeout(settleTimer);
      if (raf) cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [animatable, target, value, index]);

  return (
    <div className="stat">
      <div className="mono stat-label">{label}</div>
      <span className="stat-value">{shown}</span>
      {note ? <div className="stat-note">{note}</div> : null}
    </div>
  );
}
